import { useCallback, useState } from "react";

import { BlockBlobClient } from "@azure/storage-blob";
import { useDispatch } from "react-redux";

import { showError, showSuccess } from "@utils/notification";

import type { DocSorterFileSchema } from "../api/generated/types.gen";
import {
  DEFAULT_ALLOWED_EXTENSIONS,
  DEFAULT_MAX_FILE_SIZE,
} from "../constants/file";
import { runWithUploadMarker } from "./mutations/upload-marker";
import { useUploadFile } from "./mutations/use-files";
import { fetchFileUploadSasUrl } from "./queries/use-files";

export type UploadFile = {
  id: string;
  file: File;
  category: string;
  caseNumber: string;
};

export type FileUploadOptions = {
  /** Array of allowed file extensions (without dots, e.g., ['doc', 'docx']) */
  allowedExtensions?: readonly string[];
  /** Maximum file size in bytes */
  maxFileSize?: number;
};

/**
 * Custom hook for uploading files to Azure Blob Storage and storing metadata in the database
 *
 * @param options - Configuration options for file validation
 * @param options.allowedExtensions - Array of allowed file extensions (default: doc, docx)
 * @param options.maxFileSize - Maximum file size in bytes (default: 10MB)
 *
 * @example
 * // Use with default settings (10MB, Word documents only)
 * const { uploadFile, isUploading } = useFileUpload();
 *
 * @example
 * // Use with custom file size limit for Word documents
 * import { FILE_EXTENSIONS, FILE_SIZES } from '@/constants/file';
 * const { uploadFile, isUploading } = useFileUpload({
 *   allowedExtensions: FILE_EXTENSIONS.WORD_DOCUMENTS,
 *   maxFileSize: FILE_SIZES.MB_25
 * });
 */

export const useFileUpload = (options: FileUploadOptions = {}) => {
  const {
    allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
  } = options;

  const dispatch = useDispatch();

  const [isUploading, setIsUploading] = useState(false);

  const uploadFileMutation = useUploadFile();

  const uploadFile = useCallback(
    async (uploadFile: UploadFile): Promise<DocSorterFileSchema> => {
      const { file, category, caseNumber } = uploadFile;

      try {
        setIsUploading(true);

        // Validate file
        const fileExtension = file.name.split(".").pop()?.toLowerCase();

        if (
          !fileExtension ||
          !(allowedExtensions as readonly string[]).includes(fileExtension)
        ) {
          throw new Error(
            `File type not supported. Allowed types: ${allowedExtensions.join(", ")}`
          );
        }

        if (file.size > maxFileSize) {
          const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
          throw new Error(`File size exceeds ${maxSizeMB}MB limit`);
        }

        // Get a write-only, file-scoped SAS URL from the backend
        const { url: uploadUrl, upload_id: uploadId } = await fetchFileUploadSasUrl({
          case_number: caseNumber,
          classification: category,
          filename: file.name,
        });

        const uploadResult = await runWithUploadMarker(uploadId, async () => {
          const blobClient = new BlockBlobClient(uploadUrl);
          await blobClient.uploadData(file);

          return uploadFileMutation.mutateAsync({
            filename: file.name,
            case_number: caseNumber,
            classification: category,
            upload_id: uploadId,
          });
        });

        showSuccess(`File "${file.name}" uploaded successfully`, dispatch);

        return uploadResult.file;
      } catch (error: unknown) {
        const errorMessage =
          (error as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ||
          (error as Error)?.message ||
          "Upload failed";

        showError(`Failed to upload "${file.name}": ${errorMessage}`, dispatch);
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [dispatch, uploadFileMutation, allowedExtensions, maxFileSize]
  );

  return {
    uploadFile,
    isUploading: isUploading || uploadFileMutation.isPending,
  };
};
